import { ChatEvent, ChatMessages } from "../reducers/messagesSlice"
import { useAppSelector } from "../reducers/hooks"
import { selectMessages } from "../reducers/messagesSliceSelectors"
import { selectChatMessagesWithAppliedRetractions } from "../reducers/chatsSliceSelectors"
import ChatHelpers from "../lib/chatHelpers"
import { messagesForChatList } from "../lib/agentThread"
import { useAgentForDriverPerChat } from "./useAgentForDriverPerChat"
import useSlickAISilencePredicate from "./useSlickAISilencePredicate"

const useLastChatsEvent = (
  chatJids: string[],
): Record<string, ChatEvent | undefined> => {
  const allMessages = useAppSelector(selectMessages)
  const isSilenced = useSlickAISilencePredicate()
  const hideDriverAgentThread = useAgentForDriverPerChat()
  return chatJids.reduce<Record<string, ChatEvent | undefined>>((acc, jid) => {
    acc[jid] = findLastChatEvent(
      allMessages[jid],
      isSilenced(jid),
      hideDriverAgentThread,
    )
    return acc
  }, {})
}

export const useLastChatEvent = (chatJid: string): ChatEvent | undefined => {
  const allMessages = useAppSelector(selectMessages)
  const isSilenced = useSlickAISilencePredicate()
  const hideDriverAgentThread = useAgentForDriverPerChat()
  return findLastChatEvent(
    allMessages[chatJid],
    isSilenced(chatJid),
    hideDriverAgentThread,
  )
}

export const findLastChatEvent = (
  messages: ChatMessages | undefined,
  silenceSlickAI = false,
  hideDriverAgentThread = false,
) => {
  if (!messages) {
    return undefined
  }

  // Backend stamps every internal note with slick:agent, not just the
  // dispatcher↔AI side channel. Drop that thread only where the workspace
  // exists; otherwise fuel alerts and the rest vanish from the list preview.
  const appliedMessages = messagesForChatList(
    selectChatMessagesWithAppliedRetractions(messages)(),
    hideDriverAgentThread,
  )
  const lastMessage = silenceSlickAI
    ? ChatHelpers.withoutSilencedSlickAI(appliedMessages).at(-1)
    : appliedMessages.at(-1)

  if (messages?.lastNonMessageEvent) {
    if (!lastMessage) return messages.lastNonMessageEvent

    return messages.lastNonMessageEvent.timestamp > lastMessage.timestamp
      ? messages.lastNonMessageEvent
      : lastMessage
  }

  return lastMessage
}

export default useLastChatsEvent
